import React from "react";
import { ArrowLeft, Heart, ShieldCheck, Users, MapPin, Sparkles, ArrowRight } from "lucide-react";

// About page — mission, community impact, feature ecosystem
export default function About({ onBack, onContact }) {
  const features = [
    { icon: ShieldCheck, title: "Compliance Engine", text: "Real-time staff-to-child ratio tracking aligned to state licensing rules, with instant violation alerts." },
    { icon: Users, title: "Care Connections", text: "A verified caregiver marketplace with geolocation search, credential review, and direct booking." },
    { icon: Heart, title: "Family Portal", text: "Check-in/out, photo updates, e-signed documents, video meetings, and direct staff messaging." },
    { icon: MapPin, title: "Regional Oversight", text: "Multi-tier administration — national, state, and community-level oversight that scales with your organization." },
  ];

  return (
    <div className="min-h-screen bg-[#FFF8E7] dark:bg-[#1F2937]" data-testid="about-page">
      <header className="max-w-5xl mx-auto px-6 pt-8 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2.5 cursor-pointer" data-testid="about-brand" title="Back to home">
          <img src="/assets/logo.png" alt="NestTrack" className="w-10 h-10 rounded-full" />
          <span className="text-xl font-black font-heading text-[#374151] dark:text-slate-100">NestTrack</span>
        </button>
        <button onClick={onBack} data-testid="about-back-btn" className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-xs font-bold hover:border-[#F8C8DC] transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <section className="max-w-2xl mb-16">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#FCE6E9] text-[#B2607F] text-[10px] font-extrabold uppercase tracking-widest rounded-full mb-5">
            <Sparkles className="w-3 h-3" /> Our Mission
          </span>
          <h1 className="text-4xl sm:text-5xl font-black font-heading text-[#374151] dark:text-slate-100 leading-tight mb-5">
            Every child deserves a safe, well-run nest.
          </h1>
          <p className="text-base text-[#6B7280] dark:text-slate-300 leading-relaxed">
            NestTrack was built on a simple observation: childcare operators spend more time on paperwork than on children.
            We unify licensing compliance, staffing, family communication, and billing into one calm, pastel workspace —
            so daycares stay audit-ready and parents stay connected, every single day.
          </p>
        </section>

        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16" data-testid="about-impact">
          {[
            ["500+", "Children cared for daily"],
            ["50", "States supported"],
            ["99.98%", "Platform uptime"],
            ["$30/mo", "Flat facility pricing"],
          ].map(([val, label]) => (
            <div key={label} className="p-5 bg-white border border-[#FCE6E9] rounded-3xl text-center shadow-sm dark:bg-[#232D3D] dark:border-[#374457]">
              <p className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">{val}</p>
              <p className="text-[11px] font-bold text-[#9CA3AF] mt-1">{label}</p>
            </div>
          ))}
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100 mb-6">The ecosystem</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.title} className="p-6 bg-white border border-[#FCE6E9] rounded-3xl hover:border-[#F8C8DC] hover:-translate-y-0.5 transition-all duration-200 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`about-feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="w-11 h-11 rounded-full bg-[#FCE6E9] flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#E5A0BC]" />
                </span>
                <h3 className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-1.5">{f.title}</h3>
                <p className="text-sm text-[#6B7280] dark:text-slate-300 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="p-8 bg-gradient-to-br from-[#FCE6E9] to-[#E6F2FA] rounded-[2rem] text-center" data-testid="about-cta">
          <h2 className="text-2xl font-black font-heading text-[#374151] mb-3">Questions? Talk to a human.</h2>
          <p className="text-sm text-[#6B7280] mb-6 max-w-md mx-auto">Our support team answers every inquiry personally — usually within one business day.</p>
          <button onClick={onContact} data-testid="about-contact-btn" className="inline-flex items-center gap-2 px-6 py-3 bg-[#374151] hover:bg-[#2b3441] text-white rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5">
            Contact Us <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </main>
    </div>
  );
}
